import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import ComsEmbedPanel from '../components/ComsEmbedPanel';
import AccountLinkingPanel from '../components/AccountLinkingPanel';
import api from '../utils/api';
import { buildChannelSpaceUrl, isComsHubUser, loadComsHubData } from '../utils/comsHub';
import './AccountPage.css';

const AccountPage = () => {
  const { currentUser, refreshUser } = useContext(AuthContext);

  // Modal visibility
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCardHelpModal, setShowCardHelpModal] = useState(false);
  const [modalShake, setModalShake] = useState(false);

  const handleBackdropClick = () => {
    setModalShake(true);
    setTimeout(() => setModalShake(false), 500);
  };

  // Profile form
  const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', email: '', cardNumber: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  // Password form
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Applications
  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState('');

  // Staff -> Dev quick note form
  const [devNoteForm, setDevNoteForm] = useState({ title: '', tagsText: '', changes: '' });
  const [devNoteSaving, setDevNoteSaving] = useState(false);
  const [devNoteError, setDevNoteError] = useState('');
  const [devNoteSuccess, setDevNoteSuccess] = useState('');
  const [connectorConfig, setConnectorConfig] = useState(null);
  const [defaultComsappBaseUrl, setDefaultComsappBaseUrl] = useState('');
  const [myThreads, setMyThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);

  // Sync profile form when user loads
  useEffect(() => {
    if (currentUser) {
      setProfileForm({
        firstName:  currentUser.firstName  || '',
        lastName:   currentUser.lastName   || '',
        email:      currentUser.email      || '',
        cardNumber: currentUser.cardNumber || '',
      });
    }
  }, [currentUser]);

  useEffect(() => {
    loadComsHubData({
      api,
      currentUser,
      setConnectorConfig,
      setDefaultComsappBaseUrl,
      setMyThreads,
      setThreadsLoading,
    }).catch(() => {});
  }, [currentUser]);

  // Fetch applications (health programs + HR jobs)
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const [healthRes, hrRes] = await Promise.all([
          api.get('/api/health-program-applications?userOnly=true').catch(() => ({ data: { applications: [] } })),
          api.get('/api/hr/applications?userOnly=true').catch(() => ({ data: { applications: [] } }))
        ]);

        const healthApps = (healthRes.data.applications || []).map(a => ({
          _id: a._id,
          _type: 'health',
          title: a.programTitle || 'Program Application',
          subtitle: Array.isArray(a.programType) ? a.programType.join(', ') : a.programType,
          status: a.status,
          createdAt: a.createdAt,
          programId: a.healthProgramId?._id || a.healthProgramId || ''
        }));

        const hrApps = (hrRes.data.applications || []).map(a => ({
          _id: a._id,
          _type: 'hr',
          title: a.jobTitle || 'Job Application',
          subtitle: null,
          status: a.status,
          createdAt: a.createdAt
        }));

        const merged = [...healthApps, ...hrApps].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        setApplications(merged);
      } catch (err) {
        console.error('Error fetching applications:', err);
        setAppsError('Could not load applications.');
      } finally {
        setAppsLoading(false);
      }
    };
    fetchApplications();
  }, []);

  const openEditModal = () => {
    setProfileForm({
      firstName:  currentUser?.firstName  || '',
      lastName:   currentUser?.lastName   || '',
      email:      currentUser?.email      || '',
      cardNumber: currentUser?.cardNumber || '',
    });
    setProfileError('');
    setProfileSuccess('');
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setProfileError('');
    setProfileSuccess('');
  };

  const openPasswordModal = () => {
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordSuccess('');
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError('');
    if (!profileForm.firstName.trim() || !profileForm.lastName.trim()) {
      setProfileError('First and last name are required.');
      return;
    }
    if (!profileForm.email.trim()) {
      setProfileError('Email is required.');
      return;
    }
    setProfileSaving(true);
    try {
      await api.put('/api/users/me/profile', {
        firstName:  profileForm.firstName.trim(),
        lastName:   profileForm.lastName.trim(),
        email:      profileForm.email.trim(),
        cardNumber: profileForm.cardNumber.trim(),
      });
      await refreshUser();
      setProfileSuccess('Profile updated!');
      setTimeout(() => { setProfileSuccess(''); closeEditModal(); }, 1200);
    } catch (err) {
      setProfileError(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required.');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }
    setPasswordSaving(true);
    try {
      await api.put('/api/users/me/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword:     passwordForm.newPassword,
      });
      setPasswordSuccess('Password changed!');
      setTimeout(() => { setPasswordSuccess(''); closePasswordModal(); }, 1200);
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const statusLabel = (status) => {
    const map = {
      // Health program statuses
      pending:       { label: 'Pending Review', cls: 'status-pending' },
      reviewing:     { label: 'Under Review',   cls: 'status-reviewing' },
      approved:      { label: 'Approved',       cls: 'status-approved' },
      rejected:      { label: 'Not Approved',   cls: 'status-rejected' },
      incomplete:    { label: 'Incomplete',     cls: 'status-incomplete' },
      // HR job statuses
      submitted:     { label: 'Submitted',      cls: 'status-pending' },
      reviewed:      { label: 'In Review',      cls: 'status-reviewing' },
      needsMoreInfo: { label: 'Needs More Info', cls: 'status-incomplete' },
      waitlisted:    { label: 'Waitlisted',     cls: 'status-reviewing' },
    };
    return map[status] || { label: status || 'Unknown', cls: 'status-pending' };
  };

  const handleSendDevNote = async (e) => {
    e.preventDefault();
    setDevNoteError('');
    setDevNoteSuccess('');

    const parsedTags = [...new Set((devNoteForm.tagsText || '')
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean))];

    const payload = {
      title: devNoteForm.title.trim(),
      changes: devNoteForm.changes.trim(),
      tags: parsedTags,
      isPublished: true,
      isPinned: false,
      targetAudience: 'dev'
    };

    if (!payload.title || !payload.changes) {
      setDevNoteError('Title and note are required.');
      return;
    }

    setDevNoteSaving(true);
    try {
      await api.post('/api/developer-notes', payload);
      setDevNoteForm({ title: '', tagsText: '', changes: '' });
      setDevNoteSuccess('Note sent to dev team.');
    } catch (err) {
      setDevNoteError(err.response?.data?.message || 'Failed to send note to dev team.');
    } finally {
      setDevNoteSaving(false);
    }
  };

  const devSpaceUrl = buildChannelSpaceUrl(connectorConfig, defaultComsappBaseUrl, 'dev');
  const staffSpaceUrl = buildChannelSpaceUrl(connectorConfig, defaultComsappBaseUrl, 'staff');
  const devSpaceName = connectorConfig?.channels?.dev?.spaceName || connectorConfig?.channels?.dev?.label || 'Dev Space';
  const staffSpaceName = connectorConfig?.channels?.staff?.spaceName || connectorConfig?.channels?.staff?.label || 'Staff Space';
  const allowedComsUserTypes = Array.isArray(connectorConfig?.allowedUserTypes) && connectorConfig.allowedUserTypes.length > 0
    ? connectorConfig.allowedUserTypes
    : ['user', 'citizen', 'admin', 'education', 'staff'];
  const canAccessComsAccount = allowedComsUserTypes.includes(currentUser?.userType);
  const preferredComsChannel = currentUser?.userType === 'staff' || currentUser?.userType === 'admin' ? 'staff' : 'dev';
  const useComsappForDev = Boolean(devSpaceUrl);
  const showMessagingHub = canAccessComsAccount && isComsHubUser(currentUser?.userType) && (devSpaceUrl || staffSpaceUrl);

  return (
    <div className="account-page">
      <div className="account-header">
        <h1>My Account</h1>
        <p className="account-subtitle">View your profile and applications</p>
      </div>

      <div className="account-grid">
        {/* LEFT – Profile info card */}
        <div className="account-left">
          <section className="account-card">
            <h2 className="account-card-title">👤 Profile</h2>

            <div className="info-table">
              <div className="info-row">
                <span className="info-label">Name</span>
                <span className="info-value">{currentUser?.firstName} {currentUser?.lastName}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Email</span>
                <span className="info-value">{currentUser?.email || '—'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Card Number</span>
                <span className="info-value">
                  {currentUser?.cardNumber
                    ? currentUser.cardNumber
                    : <em className="not-set">Not registered</em>}
                </span>
                <button type="button" className="card-help-trigger-inline" onClick={() => setShowCardHelpModal(true)}>
                  Where is this?
                </button>
              </div>
              <div className="info-row">
                <span className="info-label">Account Type</span>
                <span className="account-badge">{currentUser?.userType || 'user'}</span>
              </div>
            </div>

            <div className="profile-actions">
              <button className="btn-edit-profile" onClick={openEditModal}>Edit Profile</button>
              <button className="btn-change-pw" onClick={openPasswordModal}>Change Password</button>
            </div>
          </section>

          {canAccessComsAccount && (
            <section className="account-card">
              <h2 className="account-card-title">💬 ComSapp Account</h2>
              <p className="account-subtitle">Link an existing ComSapp account or create a new one to access team messaging.</p>
              <AccountLinkingPanel />
            </section>
          )}

          {showMessagingHub && (
            <section className="account-card">
              <h2 className="account-card-title">💬 Team Messaging & Communications</h2>
              <p className="account-subtitle">Access ComSapp threads, post messages, and manage your external accounts.</p>

              {/* Full embedded ComSapp widget */}
              <ComsEmbedPanel 
                channel={preferredComsChannel} 
                compact={false}
              />

              <div className="field-hint" style={{ marginTop: '1.5rem' }}>
                <strong>Tips:</strong>
                <ul>
                  <li>Use the <strong>Dev space</strong> for implementation requests, blockers, and task handoff.</li>
                  <li>Use the <strong>Staff space</strong> for staff/admin coordination and internal replies.</li>
                  <li>Click <strong>Open in ComSapp</strong> to see your full profile and all communications.</li>
                  <li>If your account gets out of sync, use the <strong>Relink ComSapp Account</strong> button.</li>
                </ul>
              </div>
            </section>
          )}



          {currentUser?.userType === 'staff' && !useComsappForDev && (
            <section className="account-card">
              <h2 className="account-card-title">🧑‍💻 Send Note To Dev Team</h2>
              <form className="dev-note-form" onSubmit={handleSendDevNote}>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={devNoteForm.title}
                    onChange={(e) => setDevNoteForm((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="Quick title"
                    maxLength={160}
                  />
                </div>
                <div className="form-group">
                  <label>Tags</label>
                  <input
                    type="text"
                    value={devNoteForm.tagsText}
                    onChange={(e) => setDevNoteForm((prev) => ({ ...prev, tagsText: e.target.value }))}
                    placeholder="progress, issue, blocker"
                    maxLength={320}
                  />
                </div>
                <div className="form-group">
                  <label>Note</label>
                  <textarea
                    value={devNoteForm.changes}
                    onChange={(e) => setDevNoteForm((prev) => ({ ...prev, changes: e.target.value }))}
                    placeholder="Share what devs should know..."
                    rows={4}
                    maxLength={8000}
                  />
                </div>
                {devNoteError && <p className="form-error">{devNoteError}</p>}
                {devNoteSuccess && <p className="form-success">{devNoteSuccess}</p>}
                <div className="dev-note-actions">
                  <button type="submit" className="btn-edit-profile" disabled={devNoteSaving}>
                    {devNoteSaving ? 'Sending…' : 'Send To Dev'}
                  </button>
                  <Link to="/dev-notes" className="btn-change-pw dev-note-link">
                    Open Dev Notes Page
                  </Link>
                </div>
              </form>
            </section>
          )}
        </div>

        {/* RIGHT – Applications */}
        <div className="account-right">
          <section className="account-card account-card-applications">
            <h2 className="account-card-title">📋 My Applications</h2>

            {appsLoading ? (
              <div className="apps-loading">
                <div className="spinner-sm"></div>
                <p>Loading applications…</p>
              </div>
            ) : appsError ? (
              <p className="form-error">{appsError}</p>
            ) : applications.length === 0 ? (
              <div className="apps-empty">
                <p>You haven't submitted any applications yet.</p>
              </div>
            ) : (
              <div className="apps-list">
                {applications.map((app) => {
                  const { label, cls } = statusLabel(app.status);
                  return (
                    <div key={app._id} className="app-item">
                      <div className="app-item-main">
                        <p className="app-title">{app.title}</p>
                        <div className="app-meta-row">
                          <span className={`app-type-badge ${app._type === 'hr' ? 'type-hr' : 'type-health'}`}>
                            {app._type === 'hr' ? '👤 HR Job' : '🏥 Health Program'}
                          </span>
                          {app.subtitle && (
                            <span className="app-subtitle">{app.subtitle}</span>
                          )}
                        </div>
                      </div>
                      <div className="app-item-meta">
                        <span className={`app-status ${cls}`}>{label}</span>
                        <span className="app-date">
                          {app.createdAt
                            ? new Date(app.createdAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
                            : ''}
                        </span>
                        <div className="app-item-actions">
                          {app._type === 'health' && (
                            <>
                              <Link to={`/review-health-application/${app._id}?view=applicant`} className="app-action-link">
                                View
                              </Link>
                              {app.programId && (
                                <Link to={`/apply-health-program/${app.programId}?editApplication=${app._id}`} className="app-action-link app-action-primary">
                                  Edit
                                </Link>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* ── Edit Profile Modal ── */}
      {showEditModal && (
        <div className="modal-overlay" onClick={handleBackdropClick}>
          <div className={`modal-content account-modal${modalShake ? ' modal-shake' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Profile</h2>
              <button className="close-btn" onClick={closeEditModal}>&times;</button>
            </div>
            <form onSubmit={handleProfileSave} className="account-form">
              <div className="form-row-2">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={profileForm.firstName}
                    onChange={(e) => setProfileForm(p => ({ ...p, firstName: e.target.value }))}
                    placeholder="First name"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={profileForm.lastName}
                    onChange={(e) => setProfileForm(p => ({ ...p, lastName: e.target.value }))}
                    placeholder="Last name"
                  />
                  <small className="field-hint">Your profile data needs to match your card or there will be validation problems.</small>
                </div>
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="text"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="your@email.com"
                />
              </div>
              <div className="form-group">
                <div className="field-label-row">
                  <label>Citizen Card Number</label>
                  <button type="button" className="card-help-trigger-inline" onClick={() => setShowCardHelpModal(true)}>
                    Where is this number?
                  </button>
                </div>
                <input
                  type="text"
                  value={profileForm.cardNumber}
                  onChange={(e) => setProfileForm(p => ({ ...p, cardNumber: e.target.value }))}
                  placeholder="Leave blank if not applicable"
                />
                <small className="field-hint">Used to verify citizen status. Your card number and profile data need to match your card.</small>
              </div>

              {profileError   && <p className="form-error">{profileError}</p>}
              {profileSuccess && <p className="form-success">{profileSuccess}</p>}

              <div className="modal-form-actions">
                <button type="button" className="btn-cancel" onClick={closeEditModal}>Cancel</button>
                <button type="submit" className="btn-save" disabled={profileSaving}>
                  {profileSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Change Password Modal ── */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={handleBackdropClick}>
          <div className={`modal-content account-modal${modalShake ? ' modal-shake' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button className="close-btn" onClick={closePasswordModal}>&times;</button>
            </div>
            <form onSubmit={handlePasswordSave} className="account-form">
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(p => ({ ...p, currentPassword: e.target.value }))}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))}
                  placeholder="Repeat new password"
                  autoComplete="new-password"
                />
              </div>

              {passwordError   && <p className="form-error">{passwordError}</p>}
              {passwordSuccess && <p className="form-success">{passwordSuccess}</p>}

              <div className="modal-form-actions">
                <button type="button" className="btn-cancel" onClick={closePasswordModal}>Cancel</button>
                <button type="submit" className="btn-save" disabled={passwordSaving}>
                  {passwordSaving ? 'Changing…' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCardHelpModal && (
        <div className="modal-overlay" onClick={() => setShowCardHelpModal(false)}>
          <div className="modal-content account-modal card-help-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Citizen Card Number Help</h2>
              <button className="close-btn" onClick={() => setShowCardHelpModal(false)}>&times;</button>
            </div>
            <div className="card-help-body">
              <p className="field-hint">Enter the card number shown in the blue highlighted box on your card.</p>
              <img src="/card.jpg" alt="Citizen card sample showing the card number location" className="card-help-image" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;
